/**
 * Branded Select — Radix Select skinned to the clay design language so the
 * dropdown surface (which a native <select> can't style) is on-brand and
 * identical across both consoles.
 *
 * Two APIs:
 *  • Composable primitives (`Select`, `SelectTrigger`, `SelectValue`,
 *    `SelectContent`, `SelectItem`) — shadcn-style, for bespoke cases.
 *  • `SelectField` convenience — drop-in for the common
 *    `<select value onChange>{options.map(<option/>)}</select>` pattern.
 *
 * e2e: each item forwards `data-value` (the ORIGINAL option value) so specs can
 * pick by value — `page.locator('[role=option][data-value="DOG"]').click()` —
 * after clicking the trigger (which keeps the call site's `data-testid`).
 */
import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const Select = SelectPrimitive.Root;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      // Matches .clay-select / Input: 44px, clay inset, brand focus ring.
      'flex h-11 w-full items-center justify-between gap-2 rounded-input border-2 border-input bg-card pl-4 pr-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast',
      'placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15',
      'disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground',
      '[&>span]:line-clamp-1 [&>span]:text-left',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-fast" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      className={cn(
        'relative z-50 max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem] overflow-hidden rounded-input border-2 border-brand-outline-variant/20 bg-popover text-popover-foreground shadow-clay-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          'p-1.5',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-button py-2 pl-3 pr-8 text-sm font-medium outline-none transition-colors duration-fast',
      'focus:bg-secondary focus:text-secondary-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="absolute right-2.5 flex h-4 w-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

export { Select, SelectValue, SelectTrigger, SelectContent, SelectItem };

/* ---------------------------------------------------------------------------
 * SelectField — convenience wrapper for options-array selects.
 * Handles the native `<option value="">placeholder</option>` idiom: Radix
 * forbids an empty-string item value, so an empty option value is mapped to an
 * internal sentinel and translated back on change.
 * ------------------------------------------------------------------------- */

const EMPTY_SENTINEL = '__empty__';

export type SelectOption = { value: string; label: string };

export interface SelectFieldProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  required?: boolean;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function SelectField({
  value,
  onValueChange,
  options,
  placeholder,
  id,
  className,
  contentClassName,
  disabled,
  required,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: SelectFieldProps) {
  const hasEmptyOption = options.some((o) => o.value === '');
  // Empty value: use the sentinel when an explicit empty option exists,
  // otherwise leave it unset so the placeholder shows.
  const radixValue = value === '' ? (hasEmptyOption ? EMPTY_SENTINEL : undefined) : value;

  return (
    <Select
      value={radixValue}
      onValueChange={(v) => onValueChange(v === EMPTY_SENTINEL ? '' : v)}
      disabled={disabled}
      required={required}
    >
      <SelectTrigger id={id} data-testid={testId} aria-label={ariaLabel} className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {options.map((o) => (
          <SelectItem
            key={o.value || EMPTY_SENTINEL}
            value={o.value === '' ? EMPTY_SENTINEL : o.value}
            data-value={o.value}
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
